export interface CompanyTaxCandidate {
  id: number;
  taxType: string;
}

export interface ProductTaxScopeCandidate {
  saleOk?: boolean;
  purchaseOk?: boolean;
}

/**
 * PO decisión 8: la rejilla de presupuesto ofrece TODOS los impuestos de la compañía, igual
 * que los dos drawers de creación de línea. Ya NO se restringe por `product.taxes` /
 * `product.supplierTaxes` (productTaxIds) — esa restricción no protegía nada: el drawer ya
 * deja elegir cualquier impuesto de la compañía, así que era una barrera que se saltaba
 * cambiando de pantalla. Aplica también a productos de una sola compañía (aceptado por el PO).
 */
export function getCompanyTaxesForProduct(
  product: ProductTaxScopeCandidate | null | undefined,
  companyTaxes: CompanyTaxCandidate[],
): CompanyTaxCandidate[] {
  if (!product) {
    return [];
  }

  const allowedTaxTypes: string[] = [];

  if (product.saleOk === true) {
    allowedTaxTypes.push('Selling');
  }

  if (product.purchaseOk === true) {
    allowedTaxTypes.push('Buying');
  }

  // Los taxes de tipo 'None' siempre están disponibles, sea cual sea el producto.
  allowedTaxTypes.push('None');

  return (companyTaxes || []).filter((tax) => allowedTaxTypes.includes(tax.taxType));
}

/**
 * Cuando la línea no puede resolver su producto desde la caché (p. ej. un producto archivado:
 * el filtro global de soft-delete de EF lo excluye de la API de productos), no conocemos su
 * saleOk/purchaseOk real. Colapsar a solo 'None' escondía impuestos reales de la compañía.
 * En su lugar se asume el tipo permitido según el contexto de venta/compra de la línea —
 * quotationLines y budgetLines de venta (isCost=false) son contexto de venta (Selling+None);
 * budgetLines de costo (isCost=true) son contexto de compra (Buying+None) — delegando en
 * `getCompanyTaxesForProduct` con un producto sintético para no duplicar la regla de tipos.
 */
export function getCompanyTaxesForMissingProduct(
  companyTaxes: CompanyTaxCandidate[],
  isCostContext: boolean,
): CompanyTaxCandidate[] {
  return getCompanyTaxesForProduct(
    { saleOk: !isCostContext, purchaseOk: isCostContext },
    companyTaxes,
  );
}
